import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, ORG_TYPES } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Stethoscope, FileCheck, Shield, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const orgTypeIcons = {
  provider: Stethoscope,
  tpa: FileCheck,
  payer: Shield
};

export default function OrgSetup() {
  const navigate = useNavigate();
  const { user, setUser, setOrganization } = useAuth();
  const [selectedType, setSelectedType] = useState("provider");
  const [orgName, setOrgName] = useState(user?.organization?.name || "");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) {
      toast.error("Please enter an organization name");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/org/setup`,
        { org_type: selectedType, name: orgName },
        { withCredentials: true }
      );

      setOrganization(response.data.organization);
      toast.success("Organization setup complete!");
      navigate("/pa-requests", { replace: true });
    } catch (error) {
      console.error("Setup error:", error);
      toast.error(error.response?.data?.detail || "Failed to complete setup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-full border border-emerald-500/20 mb-4">
            <Building2 className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 font-medium">Welcome to PreAuth Pro</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Set Up Your Organization</h1>
          <p className="text-slate-400">Choose your organization type to customize your experience</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Organization Name */}
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg">Organization Name</CardTitle>
              <CardDescription className="text-slate-400">
                Enter your clinic, company, or organization name
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                data-testid="org-name-input"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g., Austin Medical Center"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </CardContent>
          </Card>

          {/* Organization Type Selection */}
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg">Organization Type</CardTitle>
              <CardDescription className="text-slate-400">
                Select the type that best describes your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              {Object.entries(ORG_TYPES).map(([type, { label, description }]) => {
                const Icon = orgTypeIcons[type];
                const isSelected = selectedType === type;

                return (
                  <button
                    key={type}
                    type="button"
                    data-testid={`org-type-${type}`}
                    onClick={() => setSelectedType(type)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? "border-emerald-500 bg-emerald-500/10"
                        : "border-slate-600 bg-slate-900/30 hover:border-slate-500"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`p-3 rounded-lg ${
                          isSelected ? "bg-emerald-500/20" : "bg-slate-700"
                        }`}
                      >
                        <Icon
                          className={`w-6 h-6 ${
                            isSelected ? "text-emerald-400" : "text-slate-400"
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3
                            className={`font-semibold ${
                              isSelected ? "text-emerald-400" : "text-white"
                            }`}
                          >
                            {label}
                          </h3>
                          {isSelected && (
                            <Check className="w-5 h-5 text-emerald-400" />
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Feature Preview based on selection */}
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-white text-lg">What You'll Get</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedType === "provider" && (
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Patient management & registration
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Create & submit prior authorization requests
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    AI-powered ICD-10/CPT code suggestions
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Real-time eligibility verification
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    PDF generation for documentation
                  </li>
                </ul>
              )}
              {selectedType === "tpa" && (
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Review queue for incoming PA requests
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Case assignment to reviewers
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Multi-provider management
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Analytics & reporting dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Escalation to decision makers
                  </li>
                </ul>
              )}
              {selectedType === "payer" && (
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Final approval/denial authority
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Medical necessity review tools
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Policy compliance checking
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Denial reason documentation
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    Appeals management
                  </li>
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="complete-setup-btn"
            disabled={loading || !orgName.trim()}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Setting up...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Complete Setup
                <ArrowRight className="w-5 h-5" />
              </span>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
